import { ProductTypeEnum } from "@jasper/client-models";
import type { UpdateProductArgs } from "@jasper/use-cases";
import { Alert, Grid, Loader, Stack, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import type { ProductStepProps } from "./types";
import { useStepBulkGroup } from "../hooks/useStepBulkGroup";
import { useRegisterStepSave } from "./useRegisterStepSave";
import { ButtonSelect } from "@jasper/forms";

type PreferencesFormValues = {
  occlusalContactTightness: string;
  proximalContactTightness: string;
  occlusalStaining: string;
  noOcclusalClearance: string;
  interproximalContactDesign: string;
  anteriorPosteriorPonticDesign: string;
};

type PreferenceField = {
  name: keyof PreferencesFormValues;
  label: string;
  data: { value: string; label: string }[];
  bridgeOnly?: boolean;
};

const PREFERENCE_FIELDS: PreferenceField[] = [
  {
    name: "occlusalContactTightness",
    label: "Occlusion",
    data: [
      { value: "HEAVY", label: "Forte" },
      { value: "MEDIUM", label: "Moyenne - par défaut" },
      { value: "LIGHT", label: "Légère" },
    ],
  },
  {
    name: "proximalContactTightness",
    label: "Contact proximal",
    data: [
      { value: "HEAVY", label: "Fort" },
      { value: "MEDIUM", label: "Moyen - par défaut" },
      { value: "LIGHT", label: "Léger" },
      { value: "NONE", label: "Aucun" },
    ],
  },
  {
    name: "occlusalStaining",
    label: "Coloration occlusale",
    data: [
      { value: "DARK", label: "Foncé" },
      { value: "MEDIUM", label: "Moyen" },
      { value: "LIGHT", label: "Clair" },
      { value: "NONE", label: "Aucun" },
    ],
  },
  {
    name: "noOcclusalClearance",
    label: "Que doit faire Jasper en cas d'espace occlusal insuffisant ?",
    data: [
      { value: "ASK_DENTIST", label: "Consulter le praticien" },
      { value: "KEEP_MANUFACTURING", label: "Poursuivre la fabrication" },
    ],
  },
  {
    name: "interproximalContactDesign",
    label: "Étendue du point de contact inter dentaire",
    data: [
      { value: "NORMAL_CONTACT_POINT", label: "Point de contact" },
      { value: "WIDE_CONTACT_SURFACE", label: "Surface de contact" },
    ],
  },
  {
    name: "anteriorPosteriorPonticDesign",
    label: "Design du pontique antérieur et postérieur",
    data: [
      { value: "MODIFIED_RIDGE_LAP", label: "Pontique crestal modifié" },
      { value: "FULL_RIDGE_LAP", label: "Complet" },
    ],
    bridgeOnly: true,
  },
];

const PREFERENCE_KEYS: (keyof PreferencesFormValues)[] = PREFERENCE_FIELDS.map(
  field => field.name
);

const PREFERENCE_DEFAULTS: PreferencesFormValues = PREFERENCE_FIELDS.reduce(
  (acc, field) => {
    acc[field.name] = field.data[0]?.value ?? "";
    return acc;
  },
  {} as PreferencesFormValues
);

type ItemPreferencesPayload = NonNullable<UpdateProductArgs["itemPreferences"]>;

const buildDefaultValues = (
  itemPreferences: Record<string, unknown> | null | undefined
): PreferencesFormValues => {
  const prefs = itemPreferences ?? {};
  return PREFERENCE_KEYS.reduce((acc, key) => {
    const value = prefs[key];
    acc[key] =
      typeof value === "string" && value ? value : PREFERENCE_DEFAULTS[key];
    return acc;
  }, {} as PreferencesFormValues);
};

export const ProductPreferencesStep = ({
  stepKey,
  productId,
  product,
  orderConfiguration,
  itemType,
  bulkGroup,
  registerStepSave,
}: ProductStepProps) => {
  const {
    isLoading: isOrderLoading,
    updateProduct,
    updateProducts,
    dentistProductPreferences,
    isLoadingDentistPreferences,
  } = orderConfiguration;
  const { isBulkMode, groupProducts } = useStepBulkGroup(product, bulkGroup);

  const productType = product?.type;
  const isBridge = productType === ProductTypeEnum.bridge;

  const dentistDefaults = useMemo(() => {
    if (!productType) {
      return undefined;
    }
    return dentistProductPreferences?.fixedProsthesisPreferences.find(
      preference => preference.itemType === itemType
    );
  }, [productType, dentistProductPreferences, itemType]);

  const hasSavedPreferences =
    !!product?.itemPreferences &&
    PREFERENCE_KEYS.some(key => !!product.itemPreferences?.[key]);

  const defaultValues = useMemo(
    () =>
      buildDefaultValues(
        hasSavedPreferences
          ? product?.itemPreferences
          : (dentistDefaults as Record<string, unknown> | undefined)
      ),
    [hasSavedPreferences, product, dentistDefaults]
  );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      const { anteriorPosteriorPonticDesign, ...commonPreferences } = value;
      const preferencesToSend = {
        ...commonPreferences,
        ...(isBridge ? { anteriorPosteriorPonticDesign } : {}),
      } as ItemPreferencesPayload;

      try {
        if (isBulkMode) {
          await updateProducts(
            groupProducts.map(groupProduct => ({
              productId: groupProduct.id,
              itemPreferences: preferencesToSend,
            }))
          );
        } else {
          await updateProduct({
            productId,
            itemPreferences: preferencesToSend,
          });
        }
      } catch {
        notifications.show({
          title: "Erreur",
          message:
            "Une erreur est survenue lors de l'enregistrement des préférences. Veuillez réessayer.",
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  useEffect(() => {
    if (product) {
      form.reset(defaultValues);
    }
  }, [product, defaultValues, form]);

  if (isOrderLoading || isLoadingDentistPreferences) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    );
  }

  if (!product) {
    return (
      <Stack p="md">
        <Alert color="red">Produit introuvable pour cette configuration.</Alert>
      </Stack>
    );
  }

  return (
    <Stack h="100%" w="100%" justify="space-between" p="md">
      <Stack gap="md">
        <Title order={2} c="primary">
          Préférences
        </Title>
        <Grid rowGap="s8">
          {PREFERENCE_FIELDS.filter(field => !field.bridgeOnly || isBridge).map(
            ({ name, label, data }) => (
              <Grid.Col span={6} key={name}>
                <form.Field name={name}>
                  {field => (
                    <ButtonSelect
                      label={label}
                      data={data}
                      value={field.state.value}
                      allowDeselect={false}
                      onBlur={() => field.handleBlur()}
                      onChange={value => {
                        // Never allow clearing a preference: ignore empty changes.
                        if (value) {
                          field.handleChange(value);
                        }
                      }}
                    />
                  )}
                </form.Field>
              </Grid.Col>
            )
          )}
        </Grid>
      </Stack>
    </Stack>
  );
};
